import { logger } from '@/features/print/logger'
import { Printer } from '@/features/print/types'
import { PrintersState, PrintersThunkApiConfig } from '@/redux/reducers/printers/types'
import { updatePersistentPrinters } from '@/redux/reducers/printers/updatePersistentPrinters'
import storageService from '@/services/storage'
import { asyncThunkCreator, buildCreateSlice } from '@reduxjs/toolkit'

const initialState: PrintersState = {
    printers: undefined,
}

export const createPrintersSlice = buildCreateSlice({
    creators: { asyncThunk: asyncThunkCreator },
})

const printersSlice = createPrintersSlice({
    name: 'printers',
    initialState,
    reducers: (create) => {
        const createThunk = create.asyncThunk.withTypes<PrintersThunkApiConfig>()

        return {
            loadPrinters: createThunk<Printer[]>(
                async (_, { rejectWithValue }) => {
                    try {
                        return await storageService.getPrinters()
                    } catch (e) {
                        logger.errorOnly(e)

                        return rejectWithValue('Failed to load printers from storage.')
                    }
                },
                {
                    fulfilled: (state, { payload: printers }) => void (state.printers = printers),
                }
            ),
            addPrinter: createThunk<Printer[], Printer>(
                async (newPrinter, thunkApi) => {
                    return updatePersistentPrinters(
                        (printers) => [...printers, newPrinter],
                        thunkApi,
                        'Failed to add printer to storage.'
                    )
                },
                {
                    fulfilled: (state, { payload: printers }) => {
                        state.printers = printers
                    },
                }
            ),
            updatePrinter: createThunk<Printer[], Printer>(
                async (updatedPrinter, thunkApi) => {
                    return updatePersistentPrinters(
                        (printers) =>
                            printers.map((printer) =>
                                printer.id === updatedPrinter.id ? updatedPrinter : printer
                            ),
                        thunkApi,
                        'Failed to update printer in storage.'
                    )
                },
                {
                    fulfilled: (state, { payload: printers }) => {
                        state.printers = printers
                    },
                }
            ),
            removePrinter: createThunk<Printer[], string>(
                async (id, thunkApi) => {
                    return updatePersistentPrinters(
                        (printers) => printers.filter((printer) => printer.id !== id),
                        thunkApi,
                        'Failed to remove printer from storage.'
                    )
                },
                {
                    fulfilled: (state, { payload: printers }) => {
                        state.printers = printers
                    },
                }
            ),
        }
    },
})

export const { loadPrinters, addPrinter, updatePrinter, removePrinter } = printersSlice.actions

export default printersSlice.reducer
